import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { TenantService } from './tenant.service';
import { AlertService } from './alert.service';

export interface TenantSwitchResult {
    success: boolean;
    error?: string;
}

@Injectable({
    providedIn: 'root',
})
export class TenantSwitchService {
    private static readonly DELAYS = {
        TENANT_SET: 200,
        UI_UPDATE: 100,
    } as const;

    constructor(
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly tenantService: TenantService,
        private readonly alertService: AlertService
    ) {}

    /**
     * Switch to a new tenant context with proper error handling and state management
     */
    async switchTenant(tenantId: string, tenants: any[], shouldNavigate: boolean = true): Promise<TenantSwitchResult> {
        try {
            // Handle special case for tenant deselection (empty string means no tenant)
            if (!tenantId || tenantId === 'select_tenant') {
                return this.handleTenantDeselection();
            }

            // Find tenant data
            const tenantData = tenants.find((tenant: any) => tenant.tenant_subdomain === tenantId);

            if (!tenantData) {
                throw new Error(`Tenant not found: ${tenantId}`);
            }

            // Update auth service with tenant info
            this.authService.setTenantInfo(tenantData);

            // Update localStorage
            localStorage.setItem('currentTenantId', tenantId);

            // Update tenant service
            this.tenantService.setCurrentTenant(tenantId);

            // Navigate if requested
            if (shouldNavigate) {
                await this.navigateToTenantRoute();
            }

            return { success: true };
        } catch (error) {
            this.alertService.showAlert('Error', 'Failed to switch tenant. Please try again.', 'error');

            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    /**
     * Handle tenant deselection (when no tenant is selected)
     */
    private async handleTenantDeselection(): Promise<TenantSwitchResult> {
        try {
            // Clear tenant selection
            this.tenantService.resetTenant();

            // Only navigate to event-hub if not already there
            if (!this.router.url.includes('/event-hub')) {
                await this.router.navigate(['/event-hub']);
            }

            // Small delay to ensure all reactive updates complete
            await this.delay(TenantSwitchService.DELAYS.UI_UPDATE);

            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: 'Failed to deselect tenant',
            };
        }
    }

    /**
     * Navigate to tenant-specific route without page reload
     */
    private async navigateToTenantRoute(): Promise<void> {
        try {
            // Add delay to ensure tenant ID is properly set
            await this.delay(TenantSwitchService.DELAYS.TENANT_SET);

            await this.router.navigate(['/event-master']);

            // Additional delay for UI update
            await this.delay(TenantSwitchService.DELAYS.UI_UPDATE);
        } catch (error) {
            throw new Error('Failed to navigate after tenant switch');
        }
    }

    /**
     * Utility method for delays
     */
    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * Check if a tenant switch is currently in progress
     */
    private isSwitchInProgress = false;

    /**
     * Get switch status
     */
    isSwitching(): boolean {
        return this.isSwitchInProgress;
    }

    /**
     * Set switch status
     */
    private setSwitchStatus(status: boolean): void {
        this.isSwitchInProgress = status;
    }

    /**
     * Safe tenant switch with duplicate prevention
     */
    async safeSwitchTenant(tenantId: string, tenants: any[], shouldNavigate: boolean = true): Promise<TenantSwitchResult> {
        if (this.isSwitchInProgress) {
            return { success: false, error: 'Tenant switch already in progress' };
        }

        this.setSwitchStatus(true);

        try {
            const result = await this.switchTenant(tenantId, tenants, shouldNavigate);
            return result;
        } finally {
            this.setSwitchStatus(false);
        }
    }
}
